import type { ArticleBlock } from '~/content/learn';
import type { ImageRef } from '~/content/types';

/**
 * Company announcements and press releases shown under /news.
 *
 * Kept in code rather than Payload so a release ships with the deploy that
 * announces it. Bodies reuse the Learn article block model, so the detail page
 * renders them with the same typography as /article/*.
 */
export interface NewsArticle {
  slug: string;
  title: string;
  /** Standfirst shown under the headline. */
  subtitle?: string;
  /** ISO date (YYYY-MM-DD), used for ordering, JSON-LD and the sitemap. */
  publishedAt: string;
  /** Human-readable date shown on the page. */
  date: string;
  /** One or two sentences for the overview list and meta description. */
  excerpt: string;
  body: ArticleBlock[];
}

export const NEWS_URL_PREFIX = 'news';

const newsArticles: NewsArticle[] = [
  {
    slug: 'nsave-fca-authorisation-emi',
    title:
      'nsave marks major milestone in journey to build global financial institution with FCA authorisation as EMI',
    subtitle: 'Expands board with two appointments to Board Risk Committee',
    publishedAt: '2026-09-28',
    date: 'Sep 28, 2026',
    excerpt:
      'nsave has been authorised by the UK Financial Conduct Authority as an Electronic Money Institution, and welcomes Peter O’Higgins and Julian Gooding to its newly formed Board Risk Committee.',
    body: [
      {
        type: 'paragraph',
        text: 'London, UK, 28th Sep 2026. nsave Ltd (“nsave”), the global financial platform built to expand access to financial services for people from emerging markets, has been authorised by the UK Financial Conduct Authority (FCA) as an Electronic Money Institution (EMI) in recognition of its robust governance, compliance and risk management. As an authorised EMI, nsave can issue electronic money and provide payment services directly to customers around the world.',
      },
      {
        type: 'paragraph',
        text: 'nsave also announces that Peter O’Higgins and Julian Gooding are joining its newly formed Board Risk Committee.',
      },
      { type: 'paragraph', text: 'Amer Baroudi, co-founder and CEO of nsave, said:' },
      {
        type: 'paragraph',
        text: '“When we started nsave, we believed that place of birth shouldn’t determine whether you have fair and easy access to the global financial system. Becoming an FCA-authorised EMI reflects the robust technology underpinning our products and the strength of our governance, compliance and risk management.',
      },
      {
        type: 'paragraph',
        text: 'We are particularly proud to have achieved this authorisation in one of the world’s most sophisticated regulatory regimes and one of the best places in the world to build ambitious, globally relevant financial companies.',
      },
      {
        type: 'paragraph',
        text: 'Furthermore, the appointment of experienced professionals of the caliber of Peter and Julian to our Board Risk Committee reflects the significant distance nsave has travelled in our journey to become a global financial institution, providing fair and easy access for emerging market customers. They’ll bring a depth of experience in fintech, finance and regulation that will be critical for nsave as we move into our next phase of growth.”',
      },
      {
        type: 'paragraph',
        text: 'The FCA EMI authorisation follows significant investment in nsave’s technology, governance, compliance and risk management capabilities, as the company has continued to develop its products and services. Since the start of the year, nsave has expanded from a global account into a financial platform adding broader payment capabilities into the wider nsave offering, while growing its customer base across multiple international markets.',
      },
      { type: 'heading', text: 'About nsave', level: 2 },
      {
        type: 'paragraph',
        text: 'nsave is a global financial platform built to expand access to financial services for people from emerging markets, and backed by TQ Ventures, Sequoia Capital, Y Combinator and other leading global technology investors.',
      },
      {
        type: 'paragraph',
        text: 'Headquartered in London, nsave provides customers with access to a range of financial products and services in emerging markets around the world with the belief that your country of birth shouldn’t be a block to the global financial system. The availability of individual products and the entity or partner providing them varies by jurisdiction.',
      },
      { type: 'heading', text: 'About Peter O’Higgins', level: 2 },
      {
        type: 'paragraph',
        text: 'Peter has more than 20 years of experience in product and finance, including scaling a fintech from start-up to global business. Peter was Revolut’s first CFO, joining when it had around 40 people and no revenue, and built the finance infrastructure that helped it raise over $315m, reach 4.5 million customers and secure a banking licence. Prior to Revolut, he spent twelve years at J.P. Morgan in London and New York, and has since been CFO at Money Dashboard and Managing Director for the UK and Europe at Brex.',
      },
      { type: 'heading', text: 'About Julian Gooding', level: 2 },
      {
        type: 'paragraph',
        text: 'Julian has thirty years of experience in legal, compliance and regulatory work across global banks, private practice and regulators. A senior regulatory lawyer, he spent his career at the FCA, Credit Suisse and Magic Circle law firms, holding senior roles in Asia, Zurich and London. These included General Counsel for APAC, GC for the Corporate Legal Group and GC COO, as well as Deputy Chief Compliance Officer and Chief Compliance Officer for Regulatory Compliance.',
      },
      { type: 'heading', text: 'Regulatory information', level: 2 },
      {
        type: 'paragraph',
        text: 'nsave Ltd is authorised by the Financial Conduct Authority as an Electronic Money Institution under the Electronic Money Regulations 2011 (FRN 1047531).',
      },
      { type: 'heading', text: 'Media enquiries', level: 2 },
      {
        type: 'paragraph',
        text: 'press@nsave.com',
        inline: [{ text: 'press@nsave.com', href: 'mailto:press@nsave.com' }],
      },
    ],
  },
];

/** Newest first. */
export function listNewsArticles(): NewsArticle[] {
  return [...newsArticles].sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
}

export function getNewsArticle(slug: string): NewsArticle | undefined {
  return newsArticles.find((article) => article.slug === slug);
}

export function newsArticleHref(article: Pick<NewsArticle, 'slug'>): string {
  return `/${NEWS_URL_PREFIX}/${article.slug}`;
}

export interface PressItem {
  outlet: string;
  title: string;
  href: string;
  publishedAt: string;
  date: string;
  image?: ImageRef;
}

const pressItems: PressItem[] = [
  {
    outlet: 'FinTech Futures',
    title: 'nsave lands EMI approval, bolsters board risk committee',
    href: 'https://www.fintechfutures.com/regulatory-actions/nsave-lands-emi-approval-bolsters-board-risk-committee',
    publishedAt: '2026-09-29',
    date: '29 September 2026',
  },
  {
    outlet: 'Tech.eu',
    title:
      'Startup nsave is bringing international banking access to Syrians shut out of the financial system',
    href: 'https://tech.eu/2026/05/15/startup-nsave-is-bringing-international-banking-access-to-syrians-shut-out-of-the-financial-system/',
    publishedAt: '2026-05-15',
    date: '15 May 2026',
    image: {
      src: '/images/news/press/techeu-syria.webp',
      alt: 'nsave co-founder and CEO Amer Baroudi',
      width: 880,
      height: 495,
    },
  },
  {
    outlet: 'The Paypers',
    title: 'nsave launches financial services for Syrians',
    href: 'https://thepaypers.com/fintech/news/nsave-launches-financial-services-for-syrians-in-two-phases',
    publishedAt: '2026-05-15',
    date: '15 May 2026',
    image: {
      src: '/images/news/press/paypers-syria.webp',
      alt: 'The Paypers news graphic',
      width: 880,
      height: 440,
    },
  },
  {
    outlet: 'Financial IT',
    title: 'nsave becomes first fintech to offer Syrians full financial access',
    href: 'https://financialit.net/news/digital-banking/nsave-becomes-first-fintech-offer-syrians-full-financial-access-inbound',
    publishedAt: '2026-05-15',
    date: '15 May 2026',
  },
  {
    outlet: 'IBS Intelligence',
    title: 'Fintech nsave expands cross-border transfers into Syria',
    href: 'https://ibsintelligence.com/ibsi-news/fintech-nsave-expands-cross-border-transfers-into-syria/',
    publishedAt: '2026-05-15',
    date: '15 May 2026',
  },
  {
    outlet: 'Neobanque.ch',
    title: 'nsave: the fintech fighting financial exclusion for 700 million people',
    href: 'https://neobanque.ch/blog/nsave-fintech-financial-inclusion/',
    publishedAt: '2026-03-01',
    date: '1 March 2026',
  },
  {
    outlet: 'FinDev Gateway',
    title: 'nsave launches investment platform to combat financial exclusion',
    href: 'https://www.findevgateway.org/news/nsave-launches-investment-platform-to-combat-financial-exclusion',
    publishedAt: '2025-01-14',
    date: '14 January 2025',
  },
  {
    outlet: 'GGBa Swiss',
    title: 'Geneva-based fintech nsave raises USD 18 million',
    href: 'https://ggba.swiss/en/geneva-based-fintech-nsave-raises-usd-18-million/',
    publishedAt: '2025-01-13',
    date: '13 January 2025',
    image: {
      src: '/images/news/press/ggba-series-a.webp',
      alt: 'nsave co-founders Amer Baroudi and Abdallah AbuHashem',
      width: 880,
      height: 605,
    },
  },
  {
    outlet: 'Fintech News Switzerland',
    title: 'nsave launches investment product and raises $18M in Series A',
    href: 'https://fintechnews.ch/london/nsave-launches-investment-product-raises-18m-series-a/73754/',
    publishedAt: '2025-01-13',
    date: '13 January 2025',
  },
  {
    outlet: 'The Paypers',
    title: 'nsave secures EUR 17.5 million in a funding round',
    href: 'https://thepaypers.com/fintech/news/nsave-secures-a-eur-175-million-in-a-funding-round',
    publishedAt: '2025-01-13',
    date: '13 January 2025',
    image: {
      src: '/images/news/press/paypers-funding.webp',
      alt: 'Funding illustration',
      width: 880,
      height: 460,
    },
  },
  {
    outlet: 'Bloomberg',
    title: 'Sequoia, Y Combinator join fundraising by UK-Swiss fintech startup nsave',
    href: 'https://www.bloomberg.com/news/articles/2025-01-10/sequoia-y-combinator-join-fundraising-by-uk-swiss-fintech-startup-nsave',
    publishedAt: '2025-01-10',
    date: '10 January 2025',
  },
  {
    outlet: 'FinTech Futures',
    title: 'Fintech start-up nsave bags $18m Series A funding, launches new investment offering',
    href: 'https://www.fintechfutures.com/venture-capital-funding/fintech-start-up-nsave-bags-18m-series-a-funding-launches-new-investment-offering',
    publishedAt: '2025-01-13',
    date: '13 January 2025',
  },
  {
    outlet: 'Finextra',
    title: 'Swiss fintech startup nsave secures $18 million Series A',
    href: 'https://www.finextra.com/newsarticle/45301/swiss-fintech-startup-nsave-secures-18-million-series-a',
    publishedAt: '2025-01-10',
    date: '10 January 2025',
  },
  {
    outlet: 'FF News',
    title:
      'Fintech nsave launches investment platform offering people from distressed economies protection from inflation',
    href: 'https://ffnews.com/news/fintech-nsave-launches-investment-platform-offering-people-from-distressed-economies-protection-from-inflation-with-compliant-and-safe-investments-abroad',
    publishedAt: '2025-01-10',
    date: '10 January 2025',
    image: {
      src: '/images/news/press/ffnews-investment.webp',
      alt: 'FF News graphic announcing the nsave investment platform',
      width: 880,
      height: 422,
    },
  },
  {
    outlet: 'Private Banker International',
    title: 'Fintech nsave expands with global investment platform',
    href: 'https://www.privatebankerinternational.com/news/fintech-nsave-expands-with-global-investment-platform/',
    publishedAt: '2025-01-13',
    date: '13 January 2025',
  },
  {
    outlet: 'Euronews',
    title:
      'Outside of the banks: Swiss fintech startup to help customers from struggling economies',
    href: 'https://www.euronews.com/2024/04/03/outside-of-the-banks-swiss-fintech-startup-to-help-customers-from-struggling-economies',
    publishedAt: '2024-04-03',
    date: '3 April 2024',
    image: {
      src: '/images/news/press/euronews-2024.webp',
      alt: 'Hands counting banknotes',
      width: 880,
      height: 495,
    },
  },
  {
    outlet: 'FinTech Global',
    title: 'Swiss fintech nsave secures $4m in seed funding to support financial inclusivity',
    href: 'https://fintech.global/2024/03/25/swiss-fintech-nsave-secures-4m-in-seed-funding-to-support-financial-inclusivity/',
    publishedAt: '2024-03-25',
    date: '25 March 2024',
    image: {
      src: '/images/news/press/fintech-global-seed.webp',
      alt: 'Lake and snow-capped mountains in the Swiss Alps',
      width: 880,
      height: 586,
    },
  },
  {
    outlet: 'TechCrunch',
    title:
      'Swiss fintech nsave gets $4M to enable people from unstable economies to open offshore accounts',
    href: 'https://techcrunch.com/2024/03/22/nsave-gets-4m-seed-funding/',
    publishedAt: '2024-03-22',
    date: '22 March 2024',
    image: {
      src: '/images/news/press/techcrunch-seed.webp',
      alt: 'nsave co-founders Amer Baroudi and Abdallah AbuHashem',
      width: 880,
      height: 478,
    },
  },
  {
    outlet: 'Yahoo Finance',
    title: 'nsave raises $4 million in seed funding co-led by Sequoia Capital and TQ Ventures',
    href: 'https://finance.yahoo.com/news/nsave-raises-4-million-seed-130000369.html',
    publishedAt: '2024-03-22',
    date: '22 March 2024',
  },
];

export function listPressItems(): PressItem[] {
  const sorted = [...pressItems].sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
  const withImage = sorted.filter((item) => item.image);
  const withoutImage = sorted.filter((item) => !item.image);
  const imageSlots = new Set(
    withImage.map((_, i) => Math.round((i * sorted.length) / withImage.length)),
  );
  return sorted.map((_, slot) =>
    imageSlots.has(slot) ? withImage.shift()! : withoutImage.shift()!,
  );
}
